import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import Modal from './Modal';
import { useAccessControl } from '../hooks/useAccessControl';
import { 
  Eye, 
  Edit, 
  Download, 
  Share, 
  Trash2,
  FileText,
  Database,
  Image,
  Pill,
  UserCheck,
  Check,
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export type PermissionType = 'read' | 'write' | 'download' | 'share' | 'delete';
export type DataType = 'medical_records' | 'documents' | 'lab_results' | 'prescriptions' | 'imaging' | 'personal_info' | 'all';
export type PermissionStatus = 'pending' | 'approved' | 'denied' | 'revoked' | 'expired';

interface DoctorPermissionViewerProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName?: string;
  doctorId: string;
}

interface PermissionData {
  id: string;
  permission_type: PermissionType;
  data_type: DataType;
  status: PermissionStatus;
  granted_at?: string;
  expires_at?: string;
  created_at: string;
}

const DoctorPermissionViewer: React.FC<DoctorPermissionViewerProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
  doctorId
}) => {
  const { permissions, requests, loading } = useAccessControl();
  const [doctorPermissions, setDoctorPermissions] = useState<PermissionData[]>([]);
  const [doctorRequests, setDoctorRequests] = useState<PermissionData[]>([]);

  const permissionTypes = [
    {
      type: 'read' as PermissionType,
      label: 'Visualizar',
      icon: <Eye className="h-4 w-4" />,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100'
    },
    {
      type: 'write' as PermissionType,
      label: 'Editar',
      icon: <Edit className="h-4 w-4" />,
      color: 'bg-green-500',
      lightColor: 'bg-green-100'
    },
    {
      type: 'download' as PermissionType,
      label: 'Baixar',
      icon: <Download className="h-4 w-4" />,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100'
    },
    {
      type: 'share' as PermissionType,
      label: 'Compartilhar',
      icon: <Share className="h-4 w-4" />,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100'
    },
    {
      type: 'delete' as PermissionType,
      label: 'Excluir',
      icon: <Trash2 className="h-4 w-4" />,
      color: 'bg-red-500',
      lightColor: 'bg-red-100'
    }
  ];

  const dataTypes = [
    {
      type: 'medical_records' as DataType,
      label: 'Registros Médicos',
      shortLabel: 'Registros',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-blue-700'
    },
    {
      type: 'documents' as DataType,
      label: 'Documentos',
      shortLabel: 'Docs',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-green-700'
    },
    {
      type: 'lab_results' as DataType,
      label: 'Resultados de Exames',
      shortLabel: 'Exames',
      icon: <UserCheck className="h-4 w-4" />,
      color: 'text-purple-700'
    },
    {
      type: 'prescriptions' as DataType,
      label: 'Prescrições',
      shortLabel: 'Receitas',
      icon: <Pill className="h-4 w-4" />,
      color: 'text-orange-700'
    },
    {
      type: 'imaging' as DataType,
      label: 'Exames de Imagem',
      shortLabel: 'Imagens',
      icon: <Image className="h-4 w-4" />,
      color: 'text-pink-700'
    },
    {
      type: 'personal_info' as DataType,
      label: 'Informações Pessoais',
      shortLabel: 'Pessoais',
      icon: <UserCheck className="h-4 w-4" />,
      color: 'text-yellow-700'
    }
  ];

  useEffect(() => {
    if (isOpen && patientId && doctorId) {
      loadDoctorPermissions();
    }
  }, [isOpen, patientId, doctorId, permissions, requests]);

  const loadDoctorPermissions = () => {
    // Filtrar permissões do médico para este paciente específico
    const doctorPerms = permissions?.filter(p => 
      p.patient_id === patientId && p.requester_id === doctorId
    ) || [];

    // Filtrar solicitações do médico para este paciente específico
    const doctorReqs = requests?.filter(r => 
      r.patient_id === patientId && r.requester_id === doctorId
    ) || [];

    // Converter para formato padronizado
    const permsData: PermissionData[] = doctorPerms.map(p => ({
      id: p.id,
      permission_type: p.permission_type as PermissionType,
      data_type: (p as any).data_type as DataType || 'medical_records',
      status: isExpired(p.expires_at) ? 'expired' : p.status as PermissionStatus,
      granted_at: p.granted_at,
      expires_at: p.expires_at,
      created_at: p.created_at
    }));

    const reqsData: PermissionData[] = doctorReqs.map(r => ({
      id: r.id,
      permission_type: r.permission_type as PermissionType,
      data_type: (r as any).data_type as DataType || 'medical_records',
      status: r.status as PermissionStatus,
      granted_at: r.granted_at,
      expires_at: r.expires_at,
      created_at: r.created_at
    }));

    setDoctorPermissions(permsData);
    setDoctorRequests(reqsData);
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getPermissionStatus = (permission: PermissionType, dataType: DataType) => {
    // Primeiro verificar permissões ativas
    const activePermission = doctorPermissions.find(
      p => p.permission_type === permission && p.data_type === dataType && p.status === 'approved'
    );

    if (activePermission) {
      return {
        status: isExpired(activePermission.expires_at) ? 'expired' : 'approved',
        data: activePermission
      };
    }

    // Verificar solicitações pendentes
    const pendingRequest = doctorRequests.find(
      r => r.permission_type === permission && r.data_type === dataType && r.status === 'pending'
    );

    if (pendingRequest) {
      return {
        status: 'pending',
        data: pendingRequest
      };
    }

    // Verificar solicitações negadas
    const deniedRequest = doctorRequests.find(
      r => r.permission_type === permission && r.data_type === dataType && r.status === 'denied'
    );

    if (deniedRequest) {
      return {
        status: 'denied',
        data: deniedRequest
      };
    }

    // Verificar permissões revogadas
    const revokedPermission = doctorPermissions.find(
      p => p.permission_type === permission && p.data_type === dataType && p.status === 'revoked'
    );

    if (revokedPermission) {
      return {
        status: 'revoked',
        data: revokedPermission
      };
    }

    return {
      status: 'none',
      data: null
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'pending':
        return <Clock className="h-3 w-3 text-yellow-600" />;
      case 'denied':
        return <XCircle className="h-3 w-3 text-red-600" />;
      case 'revoked':
        return <XCircle className="h-3 w-3 text-gray-600" />;
      case 'expired':
        return <AlertCircle className="h-3 w-3 text-orange-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'border-green-300 bg-green-50';
      case 'pending':
        return 'border-yellow-300 bg-yellow-50';
      case 'denied':
        return 'border-red-300 bg-red-50';
      case 'revoked':
        return 'border-gray-300 bg-gray-50';
      case 'expired':
        return 'border-orange-300 bg-orange-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Ativa';
      case 'pending':
        return 'Pendente';
      case 'denied':
        return 'Negada';
      case 'revoked':
        return 'Revogada';
      case 'expired':
        return 'Expirada';
      default:
        return 'Sem acesso';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true, 
        locale: ptBR 
      });
    } catch {
      return 'Data inválida';
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="max-w-[95vw]"
      title={`Permissões de Acesso - ${patientName || 'Paciente'}`}
    >
      <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Carregando permissões...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Legend */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Legenda de Status</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-xs">Ativa</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-xs">Pendente</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-xs">Negada</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <span className="text-xs">Expirada</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <XCircle className="h-4 w-4 text-gray-600" />
                    <span className="text-xs">Revogada</span>
                  </div>
                </div>
              </div>

              {/* Permission Matrix */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Matriz de Permissões
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Status das permissões por tipo de acesso e categoria de dados
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="w-full">
                    {/* Header com tipos de dados */}
                    <div className="grid grid-cols-[120px_repeat(6,1fr)] gap-2 mb-4">
                      <div className="text-xs font-medium text-gray-500 p-2">
                        Permissões / Dados
                      </div>
                      {dataTypes.map((dataType) => (
                        <div key={dataType.type} className="text-center p-2">
                          <div className={`flex flex-col items-center space-y-1 ${dataType.color}`}>
                            {dataType.icon}
                            <span className="text-xs font-medium leading-tight">
                              {dataType.shortLabel}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Grid de permissões */}
                    <div className="space-y-2">
                      {permissionTypes.map((permission) => (
                        <div key={permission.type} className="grid grid-cols-[120px_repeat(6,1fr)] gap-2">
                          {/* Header da linha - tipo de permissão */}
                          <div className="flex items-center space-x-2 p-2">
                            <div className={`p-1.5 rounded-lg text-white ${permission.color}`}>
                              {permission.icon}
                            </div>
                            <span className="text-xs font-medium text-gray-700 leading-tight">
                              {permission.label}
                            </span>
                          </div>

                          {/* Células do grid */}
                          {dataTypes.map((dataType) => {
                            const permissionStatus = getPermissionStatus(permission.type, dataType.type);
                            const statusColor = getStatusColor(permissionStatus.status);
                            const statusIcon = getStatusIcon(permissionStatus.status);
                            
                            return (
                              <div
                                key={`${permission.type}-${dataType.type}`}
                                className={`
                                  relative rounded-md border-2 transition-all duration-200 p-2 min-h-[50px]
                                  flex items-center justify-center
                                  ${statusColor}
                                `}
                                title={`${permission.label} ${dataType.label}: ${getStatusLabel(permissionStatus.status)}${
                                  permissionStatus.data?.expires_at ? ` - Expira ${formatDate(permissionStatus.data.expires_at)}` : ''
                                }`}
                              >
                                <div className="flex flex-col items-center space-y-1">
                                  {statusIcon}
                                  {permissionStatus.data?.expires_at && permissionStatus.status === 'approved' && (
                                    <div className="flex items-center space-x-1">
                                      <Calendar className="h-2 w-2 text-gray-500" />
                                      <span className="text-xs text-gray-600">
                                        {formatDate(permissionStatus.data.expires_at)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Permissões Ativas</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {doctorPermissions.filter(p => p.status === 'approved' && !isExpired(p.expires_at)).length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-yellow-100 p-2 rounded-lg">
                        <Clock className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Solicitações Pendentes</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {doctorRequests.filter(r => r.status === 'pending').length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Permissões Expiradas</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {doctorPermissions.filter(p => isExpired(p.expires_at)).length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
};

export default DoctorPermissionViewer;